import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { ArrowsClockwise } from "@phosphor-icons/react";
import api, { apiErr } from "../api";
import { FounderShell, StatusPill, MetricCard, MetricValue } from "../components/founder";

const Row = ({ label, children, testid }) => (
  <div className="flex items-center justify-between border-b border-ff-brass/10 py-2 text-sm" data-testid={testid}>
    <span className="text-ff-muted">{label}</span>
    <span className="text-ff-parchment">{children}</span>
  </div>
);

export default function FounderSystemHealth() {
  const [h, setH] = useState(null);

  const load = useCallback(() => {
    setH(null);
    api.get("/founder/system-health").then((r) => setH(r.data)).catch((e) => toast.error(apiErr(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <FounderShell>
      <div className="space-y-4" data-testid="founder-health-page">
        <div className="flex items-center justify-between">
          <h1 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">System Health</h1>
          <button onClick={load} data-testid="health-refresh" className="flex items-center gap-1.5 rounded-sm border border-ff-brass/30 px-3 py-1.5 text-sm text-ff-brass hover:border-ff-gold hover:text-ff-gold"><ArrowsClockwise size={14} /> Refresh</button>
        </div>

        {!h ? <div className="ff-panel rounded-md p-8 text-center text-ff-muted">Checking systems…</div> : (
          <>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4" data-testid="health-states">
              <div className="rounded-md border border-ff-brass/20 bg-ff-bg2/60 p-4" data-testid="health-api"><div className="text-[11px] uppercase tracking-widest text-ff-brass">API</div><div className="mt-1.5"><StatusPill state={h.api.state} /></div><div className="mt-1 text-xs text-ff-muted">{h.api.latency_ms} ms</div></div>
              <div className="rounded-md border border-ff-brass/20 bg-ff-bg2/60 p-4" data-testid="health-mongo"><div className="text-[11px] uppercase tracking-widest text-ff-brass">MongoDB</div><div className="mt-1.5"><StatusPill state={h.mongodb.state} /></div></div>
              <div className="rounded-md border border-ff-brass/20 bg-ff-bg2/60 p-4" data-testid="health-cards"><div className="text-[11px] uppercase tracking-widest text-ff-brass">Card Render</div><div className="mt-1.5"><StatusPill state={h.card_render.state} /></div><div className="mt-1 text-xs text-ff-muted"><MetricValue metric={h.card_render.success_rate} suffix="%" /></div></div>
              <div className="rounded-md border border-ff-brass/20 bg-ff-bg2/60 p-4" data-testid="health-season"><div className="text-[11px] uppercase tracking-widest text-ff-brass">Season Finalize</div><div className="mt-1.5"><StatusPill state={h.season_finalization.state} /></div></div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="ff-panel rounded-md p-5" data-testid="health-details">
                <h2 className="mb-2 text-lg font-semibold text-ff-gold">Infrastructure</h2>
                <Row label="Collections" testid="health-colls">{h.database.collections}</Row>
                <Row label="Wallet rollbacks">{<MetricValue metric={h.wallet_rollbacks} />}</Row>
                <Row label="Failed auth rate">{<MetricValue metric={h.failed_auth_rate} />}</Row>
                <Row label="Rate-limit events">{<MetricValue metric={h.rate_limit_events} />}</Row>
                <Row label="Storage usage">{<MetricValue metric={h.storage_usage} />}</Row>
                <Row label="Last backup">{<MetricValue metric={h.last_backup} />}</Row>
                <Row label="Build version">{h.build_version}</Row>
                <Row label="Last test result">{<MetricValue metric={h.last_test_result} />}</Row>
              </div>
              <div className="ff-panel rounded-md p-5" data-testid="health-flags">
                <h2 className="mb-2 text-lg font-semibold text-ff-gold">Feature Flags</h2>
                {Object.entries(h.feature_flags).map(([k, v]) => (
                  <Row key={k} label={k} testid={`flag-${k}`}>
                    <span className={v === "true" ? "text-emerald-300" : "text-ff-muted"}>{String(v)}</span>
                  </Row>
                ))}
              </div>
            </div>

            <p className="text-center text-xs text-ff-muted">Health checks reflect real system state. Metrics without configured monitoring show "Not configured" or "Unavailable" rather than fabricated values.</p>
          </>
        )}
      </div>
    </FounderShell>
  );
}
